// Pure helpers for the Expenses list: category of an expense, sorting, and the per-category summary. DISPLAY-ONLY - nothing here changes or
// sends an expense; the category is read from the accounting summary (accounting_expense / supplier default), exactly what the sender would use.
export const UNCATEGORIZED = 'none';

const toCents = (v) => Math.round((Number(v) || 0) * 100);

export const categoryKeyOf = (expense, accountingMap) => {
    const id = accountingMap?.[String(expense.id)]?.ledger_account_id;
    return id == null || id === '' ? UNCATEGORIZED : String(id);
};

// label(account) comes from useRompslompLabels (EN: "Office supplies (Kantoorartikelen)", NL: Dutch name)
export const categoryLabelOf = (key, accountsById, labelOf, uncategorizedLabel) => {
    if (key === UNCATEGORIZED) return uncategorizedLabel;
    const account = accountsById[key];
    return account ? labelOf(account) : `#${key}`;
};

export const supplierKeyOf = (expense) => String(expense.supplier || '').trim();

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

// Value a column sorts by: numbers numerically, dates by their ISO day, text case-insensitively. null = "no value", always sorted last.
export function sortValue(columnKey, expense, ctx) {
    switch (columnKey) {
        case 'date': return String(expense.purchase_date || '').slice(0, 10) || null;
        case 'item': return expense.items || null;
        case 'supplier': return expense.supplier || null;
        case 'category': return ctx.categoryLabel(expense);
        case 'price_incl_btw': return Number(expense.price_incl_btw ?? expense.total_price) || 0;
        case 'price_excl_btw': return expense.price_excl_btw == null ? null : Number(expense.price_excl_btw);
        case 'btw_amount': return expense.btw_amount == null ? null : Number(expense.btw_amount);
        case 'btw_rate': { const r = ctx.btwRates(expense); return r.length ? r[0] : null; }
        case 'paid': return Number(expense.amount_paid) || 0;
        case 'remaining': return Number(expense.remaining) || 0;
        case 'status': return expense.payment_status || null;
        case 'description': return expense.description || null;
        case 'attachment': return expense.attachment_url ? 1 : 0;
        default: return null;
    }
}

export function sortExpenses(list, columnKey, direction, ctx) {
    if (!columnKey) return list;
    const sign = direction === 'desc' ? -1 : 1;
    return list
        .map((e, i) => ({ e, i, v: sortValue(columnKey, e, ctx) }))
        .sort((a, b) => {
            const aNull = a.v == null || a.v === '';
            const bNull = b.v == null || b.v === '';
            if (aNull || bNull) return aNull === bNull ? a.i - b.i : (aNull ? 1 : -1);
            const cmp = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : collator.compare(String(a.v), String(b.v));
            return cmp !== 0 ? sign * cmp : a.i - b.i;
        })
        .map((x) => x.e);
}

export const SMALL_SLICE = 0.03;

/**
 * Spending per category for the given (already filtered) expenses.
 * Returns { totalCents, slices: [{ key, label, cents, share, members? }] } biggest first. Slices under 3% are merged into one "Other"
 * slice (only when at least two are that small) whose `members` lists what is inside.
 */
export function summarizeByCategory(expenses, { accountingMap, labelOfKey, amountCents, otherLabel }) {
    const byKey = new Map();
    let totalCents = 0;
    expenses.forEach((e) => {
        const cents = amountCents(e);
        const key = categoryKeyOf(e, accountingMap);
        byKey.set(key, (byKey.get(key) || 0) + cents);
        totalCents += cents;
    });
    const rows = [...byKey.entries()]
        .map(([key, cents]) => ({ key, label: labelOfKey(key), cents }))
        .filter((r) => r.cents > 0)
        .sort((a, b) => b.cents - a.cents);
    const positive = rows.reduce((s, r) => s + r.cents, 0);
    const withShare = rows.map((r) => ({ ...r, share: positive ? r.cents / positive : 0 }));
    const small = withShare.filter((r) => r.share < SMALL_SLICE);
    if (small.length < 2) return { totalCents, slices: withShare };
    const big = withShare.filter((r) => r.share >= SMALL_SLICE);
    const cents = small.reduce((s, r) => s + r.cents, 0);
    return {
        totalCents,
        slices: [...big, { key: '__other__', label: otherLabel, cents, share: positive ? cents / positive : 0, members: small }],
    };
}

// BTW-aware amount of one expense in cents (incl. = stored incl. price, excl. = stored excl. or derived), mirrors expenseBtwCents in Expenses.js
export const amountCentsIncl = (e) => toCents(e.price_incl_btw ?? e.total_price);
